import {
  ApiRegister,
  ApiRetailerPaymentType,
  ApiTransaction,
} from '@vend/types'
import { ReactNode } from 'react'
import VendNumber from 'vend-number'
import { TerminalModel } from '@lightspeed-payments/payments-terminal-sdk'
import { TimerStarterFunction } from '../hooks/timeTracking'
import { RefundMethod } from './refund'
import { LSPAY_REFUND_TRANSACTION_STATUS } from './lspayConstants'

export interface LSPAYModalOptions {
  amount: number
  outletId: string
  currency: string
  register: ApiRegister
  registerSaleId: string
  originalRegisterSalePaymentIds?: string[]
  originalSalePaidByOtherPT?: boolean
  // Total amount from the original LSPay payment(s) on the sale (for split-tender detection)
  originalLspayPaymentAmount?: number
  retailerPaymentType: ApiRetailerPaymentType
  transactionId: string
  metadata?: Metadata
  paymentIntentId?: string
  isRecovery?: boolean
  withSurcharge?: boolean
  tipEligibleAmount?: number
  // Indicates the original payment was processed via Processing Service
  originalSaleHasProcessingServicePayment?: boolean
}

export interface LSPAYPaymentOptions {
  transactionId: string
  amount: VendNumber
  outletId: string
  currency: string
  registerId: string
  retailerPaymentTypeId: string
  metadata: Metadata
}
export interface LSPAYServerDrivenModalOptions {
  amount: VendNumber
  currency: string
  register: ApiRegister
  outletId: string
  registerSaleId: string
  retailerPaymentType: ApiRetailerPaymentType
  transactionId: string
  paymentIntentId?: string
  isRecovery?: boolean
  withSurcharge?: boolean
  tipEligibleAmount?: VendNumber
  metadata?: LSPAYCreateTransactionMetadata
  pairing?: LSPAYPairing
  shouldUseSimulator?: boolean
}
export interface LSPAYCreateTransactionOptions {
  amount: VendNumber
  currency: string
  registerId: string
  retailerPaymentTypeId: string
  registerSalePaymentId: string
  withSurcharge?: boolean
  tipEligibleAmount?: VendNumber
  metadata: LSPAYCreateTransactionMetadata
}

export interface LSPAYCreateTransactionMetadata {
  register_sale_id: string
}

export interface Metadata {
  terminal_serial_number?: string
  terminal_label?: string
  order_id: string
  register_name: string
}

export type LSPAYGetTransactionOptions = {
  registerId: string
  paymentIntentId?: string
  registerSalePaymentId: string
}

export interface LSPAYCaptureOptions {
  transactionId: string
  registerId: string
  retailerPaymentTypeId: string
  data: {
    amount: VendNumber
    currency: string
    lspay_intent_id: string
    outlet_id: string
    lspay_payment_method: string
  }
}
export interface LSPAYConfirmOptions {
  registerId: string
  intentId: string
  paymentMethodId: string
}
export interface LSPAYCancelTransactionOptions {
  registerId: string
  registerSalePaymentId: string
  paymentIntentId: string
}

export interface LSPAYRetryTransactionOptions {
  currency: string
  registerId: string
  registerSalePaymentId: string
  paymentIntentId: string
  withSurcharge?: boolean
  tipEligibleAmount?: VendNumber
}

export interface LSPAYErrorMessage {
  header: string
  body: ReactNode
  action?: string
  primaryAction?: string
}

const valueToKeyMap: { [key: string]: TerminalModel } = {
  bbpos_chipper2x: 'bbpos_chipper2x',
  bbpos_wisepos_e: 'bbpos_wisepos_e',
  bbpos_wisepad3: 'bbpos_wisepad3',
  stripe_m2: 'stripe_m2',
  stripe_s700: 'stripe_s700',
  stripe_s710: 'stripe_s710',
  verifone_p400: 'verifone_p400',
  verifone_e285: 'verifone_e285',
  castle_s1f2: 'castle_s1f2',
  castle_ams1: 'castle_ams1',
  adyen_nyc1: 'adyen_nyc1',
  unknown: 'unknown',
}

// confirm this is an expected device type, otherwise return 'unknown'
export function getDeviceType(value: string | undefined): TerminalModel {
  return value ? valueToKeyMap[value] || 'unknown' : 'unknown'
}
export interface LSPAYPairing {
  registerID: string
  deviceType: string
  deviceID: string
  deviceLabel: string
  deviceSN: string
  pli: string
}

export interface LSPAYPairingData {
  lspayPairings: LSPAYPairing[]
}

export interface LSPAYPairingRequest {
  registerID?: string
}

export interface LSPAYRefundOptions {
  registerID: string
  registerName: string
  currency: string
  retailerPaymentTypeID: string
  registerSalePaymentID: string
  outletId: string
  amount: VendNumber
  trackTiming: TimerStarterFunction
  originalRegisterSalePaymentId?: string | null
  refundMethod?: RefundMethod
  unreferencedPaymentMethodID?: string
}

export interface CreateRefundOptions {
  amount: VendNumber
  currency: string
  registerId: string
  retailerPaymentTypeId: string
  registerSalePaymentId: string
  metadata: CreateRefundMetadata
  originalRegisterSalePaymentIds?: string[]
  consentCollected?: boolean
}

export interface CreateRefundMetadata {
  register_sale_id: string
}

export interface GetRefundOptions {
  registerSalePaymentId: string
  registerId: string
  setupIntentId: string
}

export interface ReprocessRefundOptions {
  setupIntentId: string
  registerId: string
  registerSalePaymentId: string
}

interface BaseRefundResponse {
  register_sale_payment_id: string
  setup_intent_id: string
  status: LSPAY_REFUND_TRANSACTION_STATUS
  reason: string
  reprocessing_available: boolean
}

// Define a type for completed refund transactions
export interface RefundCompletedResponse extends BaseRefundResponse {
  status: LSPAY_REFUND_TRANSACTION_STATUS.COMPLETED
  transaction: ApiTransaction
}

export interface RefundNotCompletedResponse extends BaseRefundResponse {
  status: Exclude<
    LSPAY_REFUND_TRANSACTION_STATUS,
    LSPAY_REFUND_TRANSACTION_STATUS.COMPLETED
  > // Exclude 'COMPLETED' status
}

export type RefundResponse =
  | RefundCompletedResponse
  | RefundNotCompletedResponse

export type CreateRefundResponse = RefundResponse
export interface SwiperErrorResponse {
  error: string
  code: number
  fields: any
}

export class SwiperError extends Error {
  public code: number
  // This is an object which can contain any metadata related to the error
  public fields: any

  constructor(message: string, code: number, fields: any) {
    super(message)
    this.code = code
    this.fields = fields
  }
}

export interface TerminalInfo {
  deviceId?: string
  deviceModel: TerminalModel
}

export interface Register {
  id: string
  name: string
}

export enum UnrefRefundReason {
  ZERO_DOLLAR_REFUNDABLE = 'ZERO_DOLLAR_REFUNDABLE',
  MULTIPLE_PAYMENTS = 'MULTIPLE_PAYMENTS',
  BLIND_REFUND = 'BLIND_REFUND',
  DIFFERENT_PAYMENT_TYPES = 'DIFFERENT_PAYMENT_TYPES',
}
